'use client'

// SUMMARY: The in-chat CHOICE card (deep-research confirm / project-output quiz). Renders the assistant's
// options as single-select buttons or multi-select checkboxes and, on submit, posts a readable message
// plus a hidden result block back into the chat (via DocumentFormContext) for the route to act on.

import { useState } from 'react'
import { useLocale } from 'next-intl'
import { Sparkles, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { encodeChoiceResult, type ChoiceKind, type ChoicePrompt } from '@/lib/ai/choice-form'
import { useDocumentFormActions } from './document-form-context'

export function ChoiceCard({ prompt }: { prompt: ChoicePrompt }) {
	const locale = useLocale()
	const de = locale === 'de'
	const actions = useDocumentFormActions()
	const [selected, setSelected] = useState<string[]>([])
	const [submitted, setSubmitted] = useState(false)

	const toggle = (key: string) => {
		setSelected((prev) =>
			prompt.multi ? (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]) : [key]
		)
	}

	const canSubmit = selected.length > 0 && !submitted && !actions?.disabled

	const submit = () => {
		if (!actions || !canSubmit) return
		const chosen = prompt.options.filter((option) => selected.includes(option.key))
		// Post a readable line + a hidden result block (stripped from the bubble). The block makes the answer
		// unambiguous to the route - a free-typed message has no block and is handled normally.
		const humanText = buildHumanText({ kind: prompt.kind, labels: chosen.map((option) => option.label), de })
		setSubmitted(true)
		actions.onSubmit(encodeChoiceResult({ humanText, kind: prompt.kind, selected, state: prompt.state }))
	}

	const submitLabel = submitted ? (de ? 'Übermittelt' : 'Submitted') : de ? 'Los geht’s' : 'Continue'

	return (
		<div className="my-3 overflow-hidden rounded-xl border border-border">
			<div className="flex items-center gap-2 border-b border-border bg-foreground/[0.03] px-3.5 py-2.5">
				<Sparkles className="h-4 w-4 shrink-0 text-primary" />
				<span className="text-sm font-semibold">{prompt.title}</span>
			</div>
			<div className="space-y-2 p-3.5">
				{prompt.options.map((option) => {
					const active = selected.includes(option.key)
					return (
						<button
							key={option.key}
							type="button"
							disabled={submitted}
							onClick={() => toggle(option.key)}
							className={cn(
								'flex w-full items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
								active ? 'border-blue-500 bg-blue-500/10' : 'border-border hover:bg-accent',
								submitted && 'cursor-default opacity-70'
							)}
						>
							{/* Custom control (not a Radix Checkbox) - the row is a <button>, so avoid button-in-button. */}
							<span
								className={cn(
									'flex h-4 w-4 shrink-0 items-center justify-center border',
									prompt.multi ? 'rounded-[4px]' : 'rounded-full',
									active ? 'border-blue-500 bg-blue-500 text-white' : 'border-input'
								)}
							>
								{active && <Check className="h-3 w-3" />}
							</span>
							<span className="min-w-0 break-words">{option.label}</span>
						</button>
					)
				})}
				<Button onClick={submit} disabled={!canSubmit} className="mt-1 w-full">
					{submitLabel}
				</Button>
			</div>
		</div>
	)
}

/** The readable message the card posts (the hidden result block is appended by encodeChoiceResult). */
function buildHumanText(options: { kind: ChoiceKind; labels: string[]; de: boolean }): string {
	const { kind, labels, de } = options
	// refine: the chosen scope narrows what the research covers.
	if (kind === 'refine') {
		const lead = de ? 'Bitte darauf eingrenzen:' : 'Please narrow it to:'
		return `${lead} ${labels.join(', ')}`
	}
	// project-output (multi): list the chosen targets.
	const lead = de ? 'Gib die Ergebnisse so aus:' : 'Serve the results as:'
	return `${lead} ${labels.join(', ')}`
}
